import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import routes from "@/routes";

import {
  Button,
  Flex,
  Card,
  App,
  Spin,
  Descriptions,
  Modal,
  Tag,
  Row,
  Col,
  Avatar,
  Typography,
  Tooltip,
} from "antd";

import TrashIcon from "@/icons/TrashIcon";
import EditIcon from "@/icons/EditIcon";
import ExternalLinkIcon from "@/icons/ExternalLinkIcon";
import UserIcon from "@/icons/UserIcon";
import LockOpenIcon from "@/icons/LockOpenIcon";

import UserModal from "@/components/modals/UserModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import BackBtn from "@/components/ui/BackBtn";
import CopyableText from "@/components/ui/СopyableText";
import UnitsTable from "@/components/tables/UnitsTable";
import BindingEmployeeModal from "@/components/modals/BindingEmployeeModal";
import ChangePasswordModal from "@/components/modals/ChangePasswordModal";

import { getItem, deleteItem, unbindingEmployee } from "@/api/users";
import { getEmployeeUnits, getEmployeesOptions } from "@/api/employees";

import { useEmployees } from "@/context/EmployeesContext";

import { getFullName, formateDate } from "@/utils";

const { Title } = Typography;

const User = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  // Данные пользователя
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [units, setUnits] = useState([]);
  const [unitsPending, setUnitsPending] = useState(true);

  const [employeesList, setEmployeesList] = useState([]);
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);

  const { employees } = useEmployees();

  const fio = useMemo(() => {
    if (!data || !data.employee) return "";
    return getFullName(
      data.employee.lastname,
      data.employee.firstname,
      data.employee.patronymic
    );
  }, [data]);

  const initials = useMemo(() => {
    if (!data || !data.employee) return "";
    return `${data.employee.lastname?.[0] ?? ""}${
      data.employee.firstname?.[0] ?? ""
    }`;
  }, [data]);

  const isDeleted = useMemo(() => {
    return data?.employee?.deleted;
  }, [data]);

  const status = employees.find(
    (emp) => emp.employee_uuid === data?.employee?.uuid
  )?.status;

  // initial fetch
  useEffect(() => {
    fetchData();
    fetchUserList();
    // eslint-disable-next-line
  }, [id]);

  // Запрос на сервер для получения данных
  const fetchData = async () => {
    setIsLoading(true);

    try {
      const data = await getItem(id);

      setData(data);
      setIsLoading(false);

      if (data.employee) {
        setUnitsPending(true);
        const employeeUnits = await getEmployeeUnits(data.employee.uuid);
        setUnits(employeeUnits);
        setUnitsPending(false);
      }
    } catch (error) {
      setIsLoading(false);
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  const fetchUserList = async () => {
    try {
      const { employees } = await getEmployeesOptions({ is_dismissed: false });

      setEmployeesList(
        employees.map((item) => {
          const { lastname, firstname, patronymic } = item.employee;
          return {
            ...item.employee,
            label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
          };
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = () => {
    setShowModal(false);
    fetchData();
  };

  const handleUnbindingEmployee = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите отвязать сотрудника ${fio} от пользователя ${data.email}?`,
      content: "",
      onOk() {
        handleConfirmUnbinding();
      },
      okText: "Отвязать",
      cancelText: "Отмена",
    });
  };

  const handleConfirmUnbinding = async () => {
    setIsLoading(true);
    try {
      await unbindingEmployee({ user_uuid: data.uuid });
      fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить пользователя ${data.email}?`,
      content: "Он больше не сможет осуществлять вход в сервис.",
      onOk() {
        handleConfirmDelete();
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем пользователя после подтверждения
  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: data.uuid });
      navigate(routes.administration.users);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25} align="flex-start">
            <Flex vertical gap={10} style={{ width: "100%" }}>
              <Breadcrumbs
                items={[
                  {
                    title: "Административная панель",
                  },
                  {
                    title: "Пользователи",
                    href: routes.administration.users,
                  },
                  {
                    title: "Информация о пользователе",
                  },
                ]}
              />

              <Flex
                align="center"
                justify="space-between"
                gap={20}
                style={{ width: "100%" }}
              >
                <BackBtn>Информация о пользователе</BackBtn>

                <Flex gap={10}>
                  <Button
                    type="text"
                    icon={<LockOpenIcon />}
                    onClick={() => setShowPasswordModal(true)}
                  >
                    Сменить пароль
                  </Button>
                  <Button
                    type="primary"
                    icon={<EditIcon />}
                    onClick={() => setShowModal(true)}
                  ></Button>
                </Flex>
              </Flex>
            </Flex>

            {/* Раздел: Данные пользователя */}
            <Card>
              <Descriptions
                column={1}
                styles={{
                  label: { display: "inline-block", minWidth: "160px" },
                  content: {
                    display: "block",
                    marginLeft: "8px",
                    fontSize: 13,
                  },
                }}
              >
                <Descriptions.Item label="Логин">
                  <CopyableText text={data.email} />
                </Descriptions.Item>
                <Descriptions.Item label="Роль">
                  <Tag>{data.role}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Раздел: Данные Сотрудника */}
            <Flex vertical gap={10}>
              <Title level={4} className="no-margin">
                Сотрудник
              </Title>
              {data.employee ? (
                <Card>
                  <Row gutter={24}>
                    <Col xs={24} sm={8} md={8}>
                      <Flex
                        vertical
                        justify="space-between"
                        align="start"
                        className="h-100"
                      >
                        <Flex justify="space-between" style={{ width: "100%" }}>
                          <Flex vertical align="start" gap={10}>
                            <Tooltip
                              title={
                                status === "online"
                                  ? "В сети"
                                  : status === "inactive"
                                    ? "Неактивен более 2 минут"
                                    : status === "offline"
                                      ? "Не в сети"
                                      : ""
                              }
                              placement="bottomLeft"
                            >
                              <div style={{ position: "relative" }}>
                                <Avatar
                                  size={48}
                                  src={status ? data.employee.avatar : null}
                                  style={{
                                    color: status ? "#E66A25" : "#777777",
                                    background: status ? "#FFEADE" : "#E9E9E9",
                                    border: "none",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {status ? initials : <UserIcon />}
                                </Avatar>

                                {(status === "online" ||
                                  status === "inactive") && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "-1px",
                                      right: "-1px",
                                      width: 48 / 3,
                                      height: 48 / 3,
                                      border: "1px solid #fff",
                                      borderRadius: "50%",
                                      backgroundColor:
                                        status === "online"
                                          ? "#2DAA55"
                                          : "#BBBBBB",
                                    }}
                                  />
                                )}
                              </div>
                            </Tooltip>

                            <div
                              style={{
                                margin: 0,
                                fontSize: 15,
                                fontWeight: 500,
                              }}
                            >
                              {fio}
                            </div>

                            {isDeleted && <Tag color="red">Удален</Tag>}
                          </Flex>
                          <Tooltip title="Перейти на страницу сотрудника">
                            <Button
                              icon={<ExternalLinkIcon />}
                              color="default"
                              variant="link"
                              href={routes.administration.employeeDetail(
                                data.employee.uuid
                              )}
                            ></Button>
                          </Tooltip>
                        </Flex>

                        <Button
                          color="default"
                          variant="link"
                          style={{ padding: 0 }}
                          onClick={handleUnbindingEmployee}
                        >
                          Отвязать от пользователя
                        </Button>
                      </Flex>
                    </Col>

                    <Col
                      xs={24}
                      sm={16}
                      md={16}
                      style={{
                        borderLeft: "1px solid #e9e9e9",
                        paddingLeft: "25px",
                      }}
                    >
                      <Descriptions
                        column={1}
                        styles={{
                          label: { display: "inline-block", minWidth: "160px" },
                          content: {
                            display: "block",
                            marginLeft: "8px",
                            fontSize: 13,
                          },
                        }}
                      >
                        <Descriptions.Item label="ФИО">
                          <CopyableText text={fio} />
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата рождения">
                          {formateDate(data.employee.birthdate) || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Табельный номер">
                          {data.employee.table_number ? (
                            <CopyableText text={data.employee.table_number} />
                          ) : (
                            "-"
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Контактные данные">
                          {data.employee.phone ? (
                            <CopyableText text={data.employee.phone} />
                          ) : (
                            "-"
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="ИНН">
                          {data.employee.inn ? (
                            <CopyableText text={data.employee.inn} />
                          ) : (
                            "-"
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Документ">
                          {data.employee.document || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Подразделение">
                          {data.employee.unit?.name || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Должность">
                          {data.employee.position?.name || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Дата приема на работу">
                          {formateDate(data.employee.employmentdate) || "-"}
                        </Descriptions.Item>
                        {data.employee.dismissaldate && (
                          <Descriptions.Item label="Дата увольнения">
                            {formateDate(data.employee.dismissaldate)}
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Col>
                  </Row>
                </Card>
              ) : (
                <Button onClick={() => setShowEmployeesModal(true)}>
                  Привязать к пользователю
                </Button>
              )}
            </Flex>

            {/* Раздел: Рабочие группы */}
            {data.employee && (
              <Flex vertical gap={10} style={{ width: "100%" }}>
                <Title level={4} className="no-margin">
                  Рабочие группы
                </Title>

                <UnitsTable
                  loading={unitsPending}
                  data={units}
                  pagination={false}
                  showActions={false}
                />
              </Flex>
            )}
            <Button
              danger
              type="link"
              icon={<TrashIcon />}
              onClick={handleDelete}
              style={{ paddingInline: 0 }}
            >
              Удалить
            </Button>
          </Flex>

          {showModal && (
            <UserModal
              data={data}
              visible={showModal}
              onCancel={() => setShowModal(false)}
              onSave={handleUpdate}
            />
          )}

          {setShowEmployeesModal && (
            <BindingEmployeeModal
              visible={showEmployeesModal}
              list={employeesList}
              user_uuid={data?.uuid}
              onSave={() => {
                setShowEmployeesModal(false);
                fetchData();
              }}
              onCancel={() => setShowEmployeesModal(false)}
            />
          )}

          {showPasswordModal && (
            <ChangePasswordModal
              uuid={id}
              visible={showPasswordModal}
              onCancel={() => setShowPasswordModal(false)}
              onSave={() => setShowPasswordModal(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default User;
